import React from 'react';
import Article from '../components/Article';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CodeBlock from '../components/CodeBlock';

function BuildingPerformantReactApplications() {
  const articleData = {
    id: 2,
    image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
    category: "React",
    title: "Building Performant React Applications: A Deep Dive",
    excerpt: "Learn how to optimize your React apps for better performance and improved user experience.",
    tags: ["React", "Performance", "JavaScript", "Frontend", "Optimization"],
    content: (
      <>
        <p>
          As React applications grow in complexity, performance concerns become increasingly important. 
          A sluggish user interface can significantly impact user experience and engagement. 
          In this deep dive, we'll explore proven strategies to optimize React applications for 
          maximum performance while maintaining code quality and developer productivity.
        </p>

        <h2>Understanding React's Rendering Process</h2>
        <p>
          Before diving into optimization techniques, it's crucial to understand how React renders components. 
          The rendering process in React consists of two main phases:
        </p>
        
        <ol className="workflow-steps">
          <li><strong>Render Phase:</strong> React builds a virtual DOM representation by calling your component functions.</li>
          <li><strong>Commit Phase:</strong> React applies the changes to the actual DOM.</li>
        </ol>
        
        <p>
          Performance issues often arise when components re-render unnecessarily. 
          Each time a component re-renders, React executes the render phase, which can be computationally expensive.
        </p>

        <h2>1. Implementing React.memo for Component Memoization</h2>
        <p>
          One of the simplest ways to prevent unnecessary re-renders is to use <code>React.memo</code>. 
          This higher-order component memoizes your component, preventing re-renders when props haven't changed:
        </p>
        
        <CodeBlock>
{`// Without memoization
function MovieCard({ title, rating, releaseDate }) {
  return (
    <div className="movie-card">
      <h3>{title}</h3>
      <p>Rating: {rating}/10</p>
      <p>Released: {releaseDate}</p>
    </div>
  );
}

// With memoization
const MemoizedMovieCard = React.memo(function MovieCard({ title, rating, releaseDate }) {
  return (
    <div className="movie-card">
      <h3>{title}</h3>
      <p>Rating: {rating}/10</p>
      <p>Released: {releaseDate}</p>
    </div>
  );
});\n
// Now MovieCard only re-renders when its props change`}
        </CodeBlock>
        
        <p>
          It's important to note that <code>React.memo</code> performs a shallow comparison of props. 
          If you're passing complex objects or functions, you may need to provide a custom comparison function:
        </p>
        
        <CodeBlock>
{`const MemoizedComponent = React.memo(MyComponent, (prevProps, nextProps) => {
  // Return true if you want to prevent the re-render
  return prevProps.complexData.id === nextProps.complexData.id;
});`}
        </CodeBlock>

        <h2>2. The useMemo and useCallback Hooks</h2>
        <p>
          When passing props to memoized child components, you need to ensure those props themselves 
          don't change on every render. This is where <code>useMemo</code> and <code>useCallback</code> come in:
        </p>
        
        <CodeBlock>
{`// Without memoization
function ParentComponent() {
  const [count, setCount] = useState(0);
  
  // This object is recreated on every render
  const userData = { name: 'John', role: 'Admin' };
  
  // This function is recreated on every render
  const handleClick = () => {
    console.log('Button clicked');
  };
  
  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>Increment</button>
      <ChildComponent userData={userData} onClick={handleClick} />
    </div>
  );
}\n
// With memoization
function OptimizedParentComponent() {
  const [count, setCount] = useState(0);
  
  // This object is only created once
  const userData = useMemo(() => ({ name: 'John', role: 'Admin' }), []);
  
  // This function is only created once
  const handleClick = useCallback(() => {
    console.log('Button clicked');
  }, []);
  
  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>Increment</button>
      <MemoizedChildComponent userData={userData} onClick={handleClick} />
    </div>
  );
}`}
        </CodeBlock>
        
        <p>
          Remember that <code>useMemo</code> is for memoizing values, while <code>useCallback</code> is specifically 
          for memoizing functions. Both accept a dependency array as their second argument, allowing you to control 
          when the memoized value should be recalculated.
        </p>

        <h2>3. Virtualization for Long Lists</h2>
        <p>
          Rendering long lists can significantly impact performance. The browser must create and maintain DOM nodes 
          for every item, even those not currently visible in the viewport. Virtualization solves this by only 
          rendering items that are visible:
        </p>
        
        <CodeBlock>
{`import { FixedSizeList } from 'react-window';

function VirtualizedList({ items }) {
  const Row = ({ index, style }) => (
    <div style={style} className="list-item">
      {items[index].name}
    </div>
  );

  return (
    <FixedSizeList
      height={400}
      width="100%"
      itemCount={items.length}
      itemSize={50}
    >
      {Row}
    </FixedSizeList>
  );
}`}
        </CodeBlock>
        
        <p>
          Libraries like <code>react-window</code> and <code>react-virtualized</code> provide robust solutions for 
          virtualizing lists, grids, and tables. This approach can dramatically improve performance when dealing with 
          thousands of items.
        </p>

        <h2>4. Code Splitting and Lazy Loading</h2>
        <p>
          Large React applications often include megabytes of JavaScript, which can lead to slow initial loads. 
          Code splitting allows you to break your bundle into smaller chunks and load them on demand:
        </p>
        
        <CodeBlock>
{`import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Instead of importing components directly
// import Dashboard from './components/Dashboard';
// import Settings from './components/Settings';

// Lazy load them
const Dashboard = lazy(() => import('./components/Dashboard'));
const Settings = lazy(() => import('./components/Settings'));

function App() {
  return (
    <Router>
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </Suspense>
    </Router>
  );
}`}
        </CodeBlock>
        
        <p>
          This approach works particularly well with React Router, allowing you to load components only when a user 
          navigates to a specific route. The <code>Suspense</code> component provides a loading fallback while the 
          component is being loaded.
        </p>

        <h2>5. State Management Optimization</h2>
        <p>
          How you structure your state can significantly impact performance. Here are some best practices:
        </p>
        
        <ul>
          <li><strong>Local vs. Global State:</strong> Keep state as local as possible. Not every piece of state needs to live in Redux or Context.</li>
          <li><strong>Normalize Complex State:</strong> For complex data structures, consider normalizing your state similar to a database structure.</li>
          <li><strong>Use Context Selectively:</strong> Split your Context into smaller, more focused providers to prevent unnecessary re-renders.</li>
        </ul>
        
        <CodeBlock>
{`// Instead of one large context
const AppContext = React.createContext();

// Split into focused contexts
const UserContext = React.createContext();
const ThemeContext = React.createContext();
const NotificationContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState('light');
  const [notifications, setNotifications] = useState([]);
  
  return (
    <UserContext.Provider value={{ user, setUser }}>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <NotificationContext.Provider value={{ notifications, setNotifications }}>
          <MainApp />
        </NotificationContext.Provider>
      </ThemeContext.Provider>
    </UserContext.Provider>
  );
}`}
        </CodeBlock>
        
        <p>
          This way, components that only need the theme won't re-render when the user or notifications change.
        </p>

        <h2>6. Optimizing Expensive Calculations</h2>
        <p>
          Some operations in your components might be computationally expensive. Use <code>useMemo</code> to cache 
          these calculations:
        </p>
        
        <CodeBlock>
{`function DataAnalytics({ data }) {
  // Without memoization - recalculated on every render
  // const processedData = processLargeDataSet(data);
  
  // With memoization - only recalculated when data changes
  const processedData = useMemo(() => {
    console.log('Processing large data set...');
    return processLargeDataSet(data);
  }, [data]);
  
  return (
    <div>
      <DataVisualization data={processedData} />
    </div>
  );
}`}
        </CodeBlock>
        
        <p>
          This pattern is especially useful for filtering, sorting, or transforming large datasets before rendering.
        </p>

        <h2>7. Web Workers for CPU-Intensive Tasks</h2>
        <p>
          JavaScript runs on a single thread, which means CPU-intensive operations can block the UI. Web Workers 
          allow you to run code in a separate thread:
        </p>
        
        <CodeBlock>
{`// worker.js
self.onmessage = function(e) {
  const result = performComplexCalculation(e.data);
  self.postMessage(result);
};

// React component
function ComplexCalculation() {
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  
  const handleCalculate = useCallback(() => {
    setIsCalculating(true);
    
    const worker = new Worker('./worker.js');
    worker.postMessage(inputData);
    
    worker.onmessage = function(e) {
      setResult(e.data);
      setIsCalculating(false);
      worker.terminate();
    };
  }, [inputData]);
  
  return (
    <div>
      <button onClick={handleCalculate} disabled={isCalculating}>
        {isCalculating ? 'Calculating...' : 'Calculate'}
      </button>
      {result && <div>Result: {result}</div>}
    </div>
  );
}`}
        </CodeBlock>
        
        <p>
          This approach is ideal for operations like image processing, complex calculations, or parsing large datasets.
        </p>

        <h2>8. Debouncing and Throttling Event Handlers</h2>
        <p>
          Events like scroll, resize, or input can fire many times per second, potentially causing performance issues. 
          Debouncing and throttling help control the rate at which your handlers execute:
        </p>
        
        <CodeBlock>
{`import { debounce, throttle } from 'lodash';

function SearchComponent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  
  // Without debounce - API called on every keystroke
  // const handleSearch = (event) => {
  //   setSearchTerm(event.target.value);
  //   fetchSearchResults(event.target.value).then(setResults);
  // };
  
  // With debounce - API called 500ms after typing stops
  const debouncedFetch = useRef(
    debounce((term) => {
      fetchSearchResults(term).then(setResults);
    }, 500)
  ).current;
  
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedFetch(value);
  };
  
  useEffect(() => {
    // Clean up the debounced function on unmount
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);
  
  return (
    <div>
      <input 
        type="text" 
        value={searchTerm} 
        onChange={handleSearch} 
        placeholder="Search..."
      />
      <ResultsList results={results} />
    </div>
  );
}`}
        </CodeBlock>
        
        <p>
          Use debouncing when you want to delay execution until after a pause (great for search inputs), and throttling 
          when you want to limit execution to a specific frequency (ideal for scroll or resize handlers).
        </p>

        <h2>9. Using Production Builds</h2>
        <p>
          Always use production builds for deployed applications. Development builds include additional warnings and 
          development-only features that slow down your application:
        </p>
        
        <CodeBlock>
{`# For Create React App
npm run build

# For custom webpack configurations
webpack --mode=production`}
        </CodeBlock>
        
        <p>
          Production builds strip out development-only code, apply minification and optimization, and enable 
          additional performance improvements.
        </p>

        <h2>10. Performance Monitoring</h2>
        <p>
          Finally, implement performance monitoring to identify and address issues proactively:
        </p>
        
        <ul>
          <li><strong>React DevTools Profiler:</strong> Visualize component render times and identify bottlenecks.</li>
          <li><strong>Lighthouse/WebPageTest:</strong> Measure overall page performance metrics.</li>
          <li><strong>Performance Monitoring Tools:</strong> Services like Sentry Performance or New Relic to track real-user performance metrics.</li>
        </ul>
        
        <p>
          Consider adding performance budgets to your development process to prevent performance regressions as your application evolves.
        </p>

        <h2>Conclusion</h2>
        <p>
          Building performant React applications requires a combination of understanding React's internals, applying 
          optimization techniques, and continuously monitoring performance. While not every optimization is necessary 
          for every application, having these tools in your toolkit allows you to address performance issues as they arise.
        </p>
        
        <p>
          Remember that premature optimization can lead to more complex, harder-to-maintain code. Start with writing 
          clean, readable code, then optimize specific areas when performance issues are identified. With these 
          strategies, you can create React applications that are both feature-rich and performant.
        </p>
      </>
    )
  };

  return (
    <div className="article-page">
      <Navbar />
      <main>
        <Article article={articleData} />
      </main>
      <Footer />
    </div>
  );
}

export default BuildingPerformantReactApplications;
